import React from 'react';
import { FilterChip } from './FilterChip';

/**
 * Active filter data
 */
interface ActiveFilter {
  key: string;
  label: string;
  value?: string;
}

/**
 * FilterToolbar Props
 */
interface FilterToolbarProps {
  /** Array of active filters to display as chips */
  filters: ActiveFilter[];
  /** Callback when a filter chip is removed */
  onRemove: (key: string) => void;
  /** Callback when "Clear All" is clicked */
  onClearAll: () => void;
  /** Callback when "Filters" button is clicked to open panel */
  onOpenPanel: () => void;
  /** Disabled state */
  disabled?: boolean;
}

/**
 * FilterToolbar Component
 * Horizontal bar displaying active filter chips, "Filters" button with badge, and "Clear All" button
 *
 * Spec: Uses .c-filter-bar layout per Carrot_Filtering_System_Specification.md
 *
 * Accessibility:
 * - Buttons have descriptive aria-labels
 * - Badge is aria-live="polite" to announce filter count changes
 * - Clear All disabled when no filters active
 */
export function FilterToolbar({
  filters,
  onRemove,
  onClearAll,
  onOpenPanel,
  disabled = false,
}: FilterToolbarProps) {
  const filterCount = filters.length;
  const hasFilters = filterCount > 0;

  return (
    <div className="c-filter-bar" role="toolbar" aria-label="Filter controls">
      {/* Active filter chips */}
      {filters.map((filter) => (
        <FilterChip
          key={filter.key}
          label={filter.label}
          value={filter.value}
          onRemove={() => onRemove(filter.key)}
          disabled={disabled}
        />
      ))}

      {/* Filters button with badge */}
      <button
        type="button"
        className="c-filter-chip"
        onClick={onOpenPanel}
        disabled={disabled}
        aria-label="Open filters panel"
      >
        <span>Filters</span>
        <span
          className={`c-filter-badge${filterCount === 0 ? ' is-zero' : ''}`}
          aria-live="polite"
          aria-label={`${filterCount} active ${filterCount === 1 ? 'filter' : 'filters'}`}
        >
          {filterCount}
        </span>
      </button>

      {/* Clear All button */}
      <button
        type="button"
        className="c-filter-reset"
        onClick={onClearAll}
        disabled={!hasFilters || disabled}
        aria-label="Clear all filters"
      >
        Clear All
      </button>
    </div>
  );
}
